import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import { Notification } from 'ksnm-common-ui/lib/Notification';
import { maxBy } from 'lodash-es';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { controlGroupEditStore } from '@stores/dictionaryStores/controlGroupsStore/controlGroupEditStore';
import { controlGroupsStore } from '@stores/dictionaryStores/controlGroupsStore/controlGroupsStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { Form } from './Form';
import { validate } from './validateSchema';

export const ControlGroupEdit = observer(() => {
  const { ctrlGroupCode } = useParams<{ ctrlGroupCode: string }>();
  const { push } = useHistory();
  const { setNew, fetch, setEditMode, controlGroup, save, isNew, isEditMode } = controlGroupEditStore;
  const { controlGroups } = controlGroupsStore;

  const permission = usePermission('CmGroupControl', controlGroupEditStore);

  useEffect(() => {
    fetchReferences(['controlGroups']);
  }, []);

  useEffect(() => {
    if (ctrlGroupCode !== NEW_ENTITY_ID) {
      fetch(ctrlGroupCode);
    } else {
      setNew(true);
    }
    return () => {
      setNew(false);
      setEditMode(false);
    };
  }, [fetch, setNew, setEditMode, ctrlGroupCode]);

  const initialValues = useMemo(() => {
    const maxGroupOrder = (maxBy(controlGroups, 'ctrlGroupOrder')?.ctrlGroupOrder ?? 0) + 1;

    return controlGroup ? { ...controlGroup, id: controlGroup.ctrlGroupCode } : { ctrlGroupOrder: maxGroupOrder };
  }, [controlGroup, controlGroups]);

  const handleSubmit = useCallback(
    (values) => {
      if ((isNew && !permission.CREATE) || (!isNew && !permission.UPDATE)) {
        Notification({
          message: `У вас нет прав для выполнения операции ${isNew ? 'создания' : 'обновления'} формы`,
        });
        return {};
      }

      const validation = validate(values);

      if (validation) {
        return validation;
      }

      (async () => {
        const value = await save({
          ctrlGroupCode: values.ctrlGroupCode,
          ctrlGroupName: values.ctrlGroupName,
          ctrlGroupOrder: values.ctrlGroupOrder,
        });

        if (isNew && value?.ctrlGroupCode) {
          push(`${RoutePath.DICTIONARY_CONTROL_GROUPS}/${value.ctrlGroupCode}`);
        }
      })();

      return undefined;
    },
    [isNew, permission.CREATE, permission.UPDATE, push, save],
  );

  return (
    <DesignLayout title="Справочник: Группы контролей" scrollEnable requiredHelp={isEditMode}>
      <FormWrapper initialValues={initialValues} onSubmit={handleSubmit} layout="vertical" compact>
        <Form />
      </FormWrapper>
    </DesignLayout>
  );
});
